import React from 'react';
import PropTypes from 'prop-types';

import {connect} from 'utils/state_manager';

import {Box, CircularProgress, Stack, TextField, Typography} from 'views/components';
import {translate} from 'views/module';
import {TASK_TYPES} from '../constants';

import * as chrono from 'chrono-node';
import moment from 'moment';

const TaskTextFields = (props) => {
  const {
    title,
    taskDescription,
    autoFocusTitle,
    admins,
    caregivers,
    clients,
    skills,
    onDueDateChange,
    onTitleChange,
    onDescriptionChange,
    onOwnersChange,
    onConnectionsChange,
    onTypeChange,
    onSkillChange,
    currentSkillRn,
    onCommandContextChange,
    commandLoading,
  } = props;

  const [activeCommand, setActiveCommand] = React.useState(null);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);

  const titleInputRef = React.useRef(null);
  const descriptionInputRef = React.useRef(null);
  const commandColors = {
    '@': '#958ACC',
    '#': '#958ACC',
    '$': '#958ACC',
    '/': '#958ACC',
    '!': '#DA7955',
  };
  const commandDefinitions = React.useMemo(() => {
    return {
      '@': admins.map((admin) => {
        const display = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.name;
        return {display, owner: admin};
      }).filter((definition) => Boolean(definition.display)),
      '#': caregivers.map((caregiver) => {
        const display = caregiver?.caregiverName ||
          caregiver?.name ||
          `${caregiver?.firstName || ''} ${caregiver?.lastName || ''}`.trim();
        return {
          display,
          connection: {
            connectionRn: caregiver?.connectionRn || caregiver?.caregiverRn,
            name: display,
            type: 'caregiver',
          },
        };
      }).filter((definition) => Boolean(definition.display)),
      '$': clients.map((client) => {
        const display = client?.clientName ||
          client?.name ||
          `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
        return {
          display,
          connection: {
            connectionRn: client?.connectionRn || client?.clientRn,
            name: display,
            type: 'client',
          },
        };
      }).filter((definition) => Boolean(definition.display)),
      '/': [...skills]
          .sort((left, right) => (left?.name || '').localeCompare(right?.name || ''))
          .map((skill) => ({
            display: skill?.name || '',
            skill,
          }))
          .filter((definition) => Boolean(definition.display)),
      '!': Object.keys(TASK_TYPES)
          .filter((taskType) => taskType !== 'UNKNOWN')
          .map((taskType) => ({
            display: taskType.toLowerCase().replace(/_/g, ' '),
            typeKey: taskType,
          })),
    };
  }, [admins, caregivers, clients, skills]);

  const resolveActiveCommand = (params) => {
    const {
      value,
      caretPosition,
      source,
      triggerSymbols,
    } = params;

    const caret = typeof caretPosition === 'number' ? caretPosition : value.length;
    const beforeCaret = value.slice(0, caret);

    for (let index = beforeCaret.length - 1; index >= 0; index -= 1) {
      const symbol = beforeCaret[index];

      if (!triggerSymbols.includes(symbol)) {
        continue;
      }

      if (index > 0 && !/\s/.test(beforeCaret[index - 1])) {
        continue;
      }

      const rawQuery = beforeCaret.slice(index + 1);

      if (rawQuery.includes('\n')) {
        return null;
      }

      if (/\s/.test(rawQuery) || !/^[a-z0-9-]*$/i.test(rawQuery)) {
        return null;
      }

      return {
        trigger: symbol,
        query: rawQuery.toLowerCase(),
        start: index,
        end: caret,
        source,
      };
    }

    return null;
  };

  const applyCommandOption = ({
    sourceValue,
    activeCommand,
    definition,
  }) => {
    if (!activeCommand || !definition) {
      return {
        nextValue: sourceValue,
        nextCaret: 0,
      };
    }

    const beforeCommand = sourceValue.slice(0, activeCommand.start);
    const afterCommand = sourceValue.slice(activeCommand.end);
    const insertedToken = `${activeCommand.trigger}${definition.display}`;
    const needsTrailingSpace = afterCommand.length > 0 && !/^\s/.test(afterCommand);
    const nextValue = `${beforeCommand}${insertedToken}${needsTrailingSpace ? ' ' : ''}${afterCommand}`;
    const nextCaret = beforeCommand.length + insertedToken.length + (needsTrailingSpace ? 1 : 0);

    return {
      nextValue,
      nextCaret,
    };
  };

  const matchCommandAt = (text, startIndex) => {
    const trigger = text[startIndex];
    const definitions = commandDefinitions[trigger] || [];

    if (!definitions?.length) {
      return null;
    }

    if (startIndex > 0 && !/\s/.test(text[startIndex - 1])) {
      return null;
    }

    const trailingText = text.slice(startIndex + 1);
    const trailingLower = trailingText.toLowerCase();
    let bestMatch = null;

    definitions.forEach((definition) => {
      if (!definition?.display) {
        return;
      }

      const matchValue = definition.display.toLowerCase();
      if (!trailingLower.startsWith(matchValue)) {
        return;
      }

      const nextChar = trailingText[matchValue.length];
      if (nextChar && !/[\s,.;:!?)]/.test(nextChar)) {
        return;
      }

      if (!bestMatch || matchValue.length > bestMatch.matchLength) {
        bestMatch = {
          trigger,
          definition,
          matchLength: matchValue.length,
        };
      }
    });

    if (!bestMatch) {
      return null;
    }

    return {
      ...bestMatch,
      index: startIndex,
      length: bestMatch.matchLength + 1,
    };
  };

  const extractRecognizedCommands = (text) => {
    const recognized = [];
    let cursor = 0;

    while (cursor < text.length) {
      const commandMatch = matchCommandAt(text, cursor);

      if (!commandMatch) {
        cursor += 1;
        continue;
      }

      recognized.push(commandMatch);
      cursor += commandMatch.length;
    }

    return recognized;
  };

  const extractDateTimeRanges = (text) => {
    const parsedResults = chrono.parse(
        text,
        new Date(),
        {forwardDate: true},
    );

    return parsedResults.map((result) => {
      const leadingOnMatch = result.text.match(/^on\s+/i);
      const leadingOffset = leadingOnMatch ? leadingOnMatch[0].length : 0;

      return {
        index: result.index + leadingOffset,
        length: result.text.length - leadingOffset,
        type: 'time',
      };
    }).filter((range) => range.length > 0);
  };

  const parseDueDateFromText = (text) => {
    if (!text.trim()) {
      return null;
    }

    const referenceDate = moment().startOf('day').toDate();
    const parsedResult = chrono.parse(text, referenceDate, {forwardDate: true})[0];

    if (!parsedResult) {
      return null;
    }

    const parsedDate = moment(parsedResult.start.date());
    const hasExplicitTime = parsedResult.start.isCertain('hour') || parsedResult.start.isCertain('minute');

    if (!parsedDate.isValid()) {
      return null;
    }

    const resolvedDate = hasExplicitTime ?
      parsedDate.second(0).millisecond(0) :
      parsedDate.hour(23).minute(59).second(0).millisecond(0);

    return resolvedDate.utc().toISOString();
  };

  const renderHighlightedText = (value, source) => {
    if (!value) {
      return null;
    }

    const commands = extractRecognizedCommands(value);
    const commandRanges = commands.map((command) => ({
      index: command.index,
      length: command.length,
      trigger: command.trigger,
      type: 'command',
    }));
    const timeRanges = extractDateTimeRanges(value).filter((timeRange) => {
      return !commandRanges.some((commandRange) => {
        const commandEnd = commandRange.index + commandRange.length;
        const timeEnd = timeRange.index + timeRange.length;
        return timeRange.index < commandEnd && commandRange.index < timeEnd;
      });
    });
    const highlightRanges = [...commandRanges, ...timeRanges]
        .sort((left, right) => left.index - right.index);
    const textSegments = [];
    let cursor = 0;

    highlightRanges.forEach((range, index) => {
      if (range.index > cursor) {
        textSegments.push({
          key: `plain-${source}-${index}-${cursor}`,
          text: value.slice(cursor, range.index),
          trigger: null,
          type: null,
        });
      }

      textSegments.push({
        key: `${range.type}-${source}-${index}-${range.index}`,
        text: value.slice(range.index, range.index + range.length),
        trigger: range.trigger || null,
        type: range.type,
      });

      cursor = range.index + range.length;
    });

    if (cursor < value.length) {
      textSegments.push({
        key: `plain-${source}-tail-${cursor}`,
        text: value.slice(cursor),
        trigger: null,
        type: null,
      });
    }

    return (
      <Box
        sx={{
          px: 0,
          py: 0,
          fontSize: source === 'title' ? '14px' : '13px',
          lineHeight: '20px',
          fontFamily: 'inherit',
          fontWeight: source === 'title' ? 500 : 400,
          letterSpacing: 0,
          color: '#2D2926',
          whiteSpace: source === 'description' ? 'pre-wrap' : 'pre',
          wordBreak: source === 'description' ? 'break-word' : 'normal',
          overflowWrap: source === 'description' ? 'anywhere' : 'normal',
          minHeight: '20px',
        }}
      >
        {
          textSegments.map((segment) => {
            const tokenSx = segment.type === 'time' ?
              {
                backgroundColor: '#F5EEDF',
                color: '#2D2926',
                borderRadius: '2px',
                boxShadow: '0 0 0 2px #F5EEDF',
              } :
              (segment.trigger ? {color: commandColors[segment.trigger]} : undefined);
            return (
              <Box
                key={segment.key}
                component='span'
                sx={tokenSx}
              >
                {segment.text}
              </Box>
            );
          })
        }
      </Box>
    );
  };

  const closeCommandMenu = () => {
    setActiveCommand(null);
    setHighlightedOptionIndex(0);
  };

  const handleCursorUpdate = (cursorState) => {
    const nextActiveCommand = resolveActiveCommand({
      value: cursorState.value,
      caretPosition: cursorState.caret,
      source: cursorState.source,
      triggerSymbols: Object.keys(commandDefinitions),
    });

    if (!nextActiveCommand) {
      closeCommandMenu();
      return;
    }

    setActiveCommand(nextActiveCommand);
  };

  const commandOptions = React.useMemo(() => {
    if (!activeCommand) {
      return [];
    }

    const query = activeCommand.query.trim().toLowerCase();
    const definitions = commandDefinitions[activeCommand.trigger] || [];

    return definitions.filter((definition) => !query || definition.display.toLowerCase().includes(query));
  }, [activeCommand, commandDefinitions]);

  const syncTaskMetaFromText = (combinedText) => {
    const commands = extractRecognizedCommands(combinedText);
    const nextOwners = [];
    const nextConnections = [];
    let selectedType = null;
    let selectedSkillRn;

    commands.forEach((command) => {
      if (command.trigger === '@' && command.definition.owner?.userRn) {
        const owner = command.definition.owner;
        if (!nextOwners.some((item) => item?.userRn === owner.userRn)) {
          nextOwners.push(owner);
        }
      }

      if ((command.trigger === '#' || command.trigger === '$') && command.definition.connection?.connectionRn) {
        const connection = command.definition.connection;
        if (!nextConnections.some((item) => item?.connectionRn === connection.connectionRn)) {
          nextConnections.push(connection);
        }
      }

      if (command.trigger === '!' && command.definition.typeKey && !selectedType) {
        selectedType = command.definition.typeKey;
      }

      if (command.trigger === '/' && command.definition.skill?.skillRn && selectedSkillRn === undefined) {
        selectedSkillRn = command.definition.skill.skillRn;
      }
    });

    onOwnersChange(nextOwners);
    onConnectionsChange(nextConnections);
    onTypeChange(selectedType || null);
    if (selectedSkillRn !== undefined && selectedSkillRn !== currentSkillRn) {
      onSkillChange(selectedSkillRn);
    }

    const parsedDueDate = parseDueDateFromText(combinedText);
    if (parsedDueDate) {
      onDueDateChange(parsedDueDate);
    }
  };

  const handleApplyCommand = (definition) => {
    if (!activeCommand || !definition) {
      return;
    }

    const commandSource = activeCommand.source === 'title' ? 'title' : 'description';
    const sourceValue = commandSource === 'title' ? title : taskDescription;
    const {nextValue} = applyCommandOption({
      sourceValue,
      activeCommand,
      definition,
    });

    if (commandSource === 'title') {
      onTitleChange(nextValue);
      syncTaskMetaFromText(`${nextValue}\n${taskDescription}`);
    } else {
      onDescriptionChange(nextValue);
      syncTaskMetaFromText(`${title}\n${nextValue}`);
    }

    closeCommandMenu();
  };

  const handleFieldKeyDown = (event) => {
    if (!activeCommand) {
      return false;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeCommandMenu();
      return true;
    }

    if (commandOptions.length === 0) {
      return false;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedOptionIndex((previousIndex) => (previousIndex + 1) % commandOptions.length);
      return true;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedOptionIndex((previousIndex) => (previousIndex - 1 + commandOptions.length) % commandOptions.length);
      return true;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      handleApplyCommand(commandOptions[highlightedOptionIndex] || commandOptions[0]);
      return true;
    }

    return false;
  };

  const getInputRefBySource = (source) => {
    return source === 'title' ? titleInputRef : descriptionInputRef;
  };

  const handleFieldCursorEvent = (source, event) => {
    handleCursorUpdate({
      source,
      value: event.target.value,
      caret: event.target.selectionStart,
    });
  };

  const handleTextInputChange = (source, nextValue) => {
    if (source === 'title') {
      onTitleChange(nextValue);
    } else {
      onDescriptionChange(nextValue);
    }

    const inputElement = getInputRefBySource(source).current;
    const resolvedCaret = inputElement?.selectionStart;

    handleCursorUpdate({
      source,
      value: nextValue,
      caret: typeof resolvedCaret === 'number' ? resolvedCaret : nextValue.length,
    });

    if (source === 'title') {
      syncTaskMetaFromText(`${nextValue}\n${taskDescription}`);
    } else {
      syncTaskMetaFromText(`${title}\n${nextValue}`);
    }
  };

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [activeCommand?.query, activeCommand?.source, activeCommand?.trigger]);

  React.useEffect(() => {
    onCommandContextChange(activeCommand);
  }, [activeCommand, onCommandContextChange]);

  const renderCommandMenu = (open) => {
    if (!open) {
      return null;
    }

    return (
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 'calc(100% + 6px)',
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid #E8E4DE',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 20px rgba(35, 32, 28, 0.12)',
          zIndex: 8,
          p: 0.5,
        }}
      >
        {
          commandLoading && commandOptions.length === 0 &&
          <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
            <CircularProgress size={16} />
          </Box>
        }
        {
          commandOptions.map((definition, optionIndex) => (
            <Box
              key={`${definition.display}-${optionIndex}`}
              component='button'
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlightedOptionIndex(optionIndex)}
              onClick={() => handleApplyCommand(definition)}
              sx={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: optionIndex === highlightedOptionIndex ? '#F7F4EF' : 'transparent',
                px: 1,
                py: 0.75,
                cursor: 'pointer',
              }}
            >
              <Typography sx={{fontSize: '14px', color: '#2D2926', lineHeight: 1.3}}>
                {definition.display}
              </Typography>
            </Box>
          ))
        }
        {
          !commandLoading && commandOptions.length === 0 &&
          <Typography variant='body2' sx={{px: 1, py: 0.75, color: '#8E887F'}}>
            {translate('tasks.Common.TaskEditor.NoMatchesFound')}
          </Typography>
        }
      </Box>
    );
  };

  return (
    <Stack spacing={1}>
      <Box sx={{position: 'relative', zIndex: 6}}>
        {
          title &&
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {renderHighlightedText(title, 'title')}
          </Box>
        }
        <TextField
          autoFocus={autoFocusTitle}
          fullWidth
          variant='standard'
          value={title}
          onChange={(value) => handleTextInputChange('title', value)}
          onKeyDown={(event) => handleFieldKeyDown(event)}
          onKeyUp={(event) => handleFieldCursorEvent('title', event)}
          onClick={(event) => handleFieldCursorEvent('title', event)}
          onFocus={(event) => handleFieldCursorEvent('title', event)}
          onBlur={closeCommandMenu}
          inputRef={(node) => {
            titleInputRef.current = node;
          }}
          placeholder={translate('tasks.Common.TaskEditor.TitlePlaceholder')}
          spellCheck={false}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              p: 0,
            },
            '& .MuiInputBase-input': {
              position: 'relative',
              zIndex: 2,
              px: 0,
              py: 0,
              fontSize: '14px',
              lineHeight: '20px',
              fontFamily: 'inherit',
              fontWeight: 500,
              letterSpacing: 0,
              whiteSpace: 'pre',
              wordBreak: 'normal',
              overflowWrap: 'normal',
              color: title ? 'transparent' : '#2D2926',
              WebkitTextFillColor: title ? 'transparent' : 'unset',
              caretColor: '#2D2926',
            },
            '& .MuiInputBase-inputMultiline': {
              p: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#A8A49E',
              opacity: 1,
            },
          }}
        />
        {renderCommandMenu(activeCommand?.source === 'title')}
      </Box>
      <Box sx={{position: 'relative', zIndex: 5}}>
        {
          taskDescription &&
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {renderHighlightedText(taskDescription, 'description')}
          </Box>
        }
        <TextField
          fullWidth
          variant='standard'
          multiline
          minRows={1}
          value={taskDescription}
          onChange={(value) => handleTextInputChange('description', value)}
          onKeyDown={(event) => handleFieldKeyDown(event)}
          onKeyUp={(event) => handleFieldCursorEvent('description', event)}
          onClick={(event) => handleFieldCursorEvent('description', event)}
          onFocus={(event) => handleFieldCursorEvent('description', event)}
          onBlur={closeCommandMenu}
          inputRef={(node) => {
            descriptionInputRef.current = node;
          }}
          placeholder={translate('tasks.Common.TaskEditor.DescriptionPlaceholder')}
          spellCheck={false}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              p: 0,
            },
            '& .MuiInputBase-input': {
              position: 'relative',
              zIndex: 2,
              px: 0,
              py: 0,
              fontSize: '13px',
              lineHeight: '20px',
              fontFamily: 'inherit',
              fontWeight: 400,
              letterSpacing: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              color: taskDescription ? 'transparent' : '#2D2926',
              caretColor: '#2D2926',
            },
            '& .MuiInputBase-inputMultiline': {
              p: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#A8A49E',
              opacity: 1,
            },
          }}
        />
        {renderCommandMenu(activeCommand?.source === 'description')}
      </Box>
    </Stack>
  );
};

TaskTextFields.propTypes = {
  onConnectionsChange: PropTypes.func.isRequired,
  onDueDateChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  onCommandContextChange: PropTypes.func,
  commandLoading: PropTypes.bool,
  onOwnersChange: PropTypes.func.isRequired,
  onSkillChange: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  autoFocusTitle: PropTypes.bool,
  admins: PropTypes.array.isRequired,
  caregivers: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
  taskDescription: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  currentSkillRn: PropTypes.string,
};

const mapStateToProps = (state) => ({
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps)(React.memo(TaskTextFields));
